
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

interface StripeAccount {
  id?: string;
  accountId: string;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
}

export function useStripeConnect() {
  const [isLoading, setIsLoading] = useState(false);
  const [accountLink, setAccountLink] = useState<string | null>(null);
  const [account, setAccount] = useState<StripeAccount | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  const getAccountStatus = async () => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "You must be logged in to check your account status",
        variant: "destructive",
      });
      return null;
    }

    try {
      setIsLoading(true);

      const { data, error } = await supabase.functions.invoke("stripe-connect", {
        body: {
          action: "get_account_status",
          userId: user.id,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      if (data.hasAccount) {
        setAccount(data.account);
      }

      return data;
    } catch (error) {
      console.error("Error getting account status:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to get account status",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const createConnectAccount = async () => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "You must be logged in to create a Stripe account",
        variant: "destructive",
      });
      return false;
    }

    try {
      setIsLoading(true);

      const { data, error } = await supabase.functions.invoke("stripe-connect", {
        body: {
          action: "create_account",
          userId: user.id,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      setAccount(data.account);
      if (data.accountLink) {
        setAccountLink(data.accountLink);
      }

      return true;
    } catch (error) {
      console.error("Error creating Connect account:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to create Stripe account",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshAccountLink = async () => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "You must be logged in to refresh your account link",
        variant: "destructive",
      });
      return false;
    }

    try {
      setIsLoading(true);

      const { data, error } = await supabase.functions.invoke("stripe-connect", {
        body: {
          action: "refresh_account_link",
          userId: user.id,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      if (data.accountLink) {
        setAccountLink(data.accountLink);
        window.location.href = data.accountLink;
      }

      return true;
    } catch (error) {
      console.error("Error refreshing account link:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to refresh account link",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    account,
    accountLink,
    getAccountStatus,
    createConnectAccount,
    refreshAccountLink,
  };
}
